// SEQ-180 phase 2, task P16: the dashboard bar above the filter bar.
//
// A native select for the dashboard, an Edit toggle, and an overflow menu
// (New, Duplicate, Rename, Share, Delete). The menu button carries
// aria-haspopup and aria-expanded and closes on Escape, matching the filter
// bar's own multi-select popover (PnlAnalyticsFilterBar.tsx).
//
// D23: only the owner or an organisation admin may edit, rename, share or
// delete. A non-owner viewing someone else's shared dashboard sees NO edit
// control at all, not a disabled one, because a disabled button invites a
// support question that the absence of one does not.
//
// D24: a delete is a hard delete with the widgets cascading, so it asks the
// user to type the dashboard's name back. A rename or a share is reversible
// in one click and asks for no confirmation beyond the dialog itself.
import { useEffect, useId, useRef, useState } from "react";
import { Copy, LayoutDashboard, MoreHorizontal, Pencil, Plus, Share2, Trash2, X } from "lucide-react";
import { Modal } from "../ui/ToastModal";
import { DEFAULT_DASHBOARD_NAME } from "../../lib/pnl/pnlWidgetSchema";
import type { PnlDashboardRow } from "../../features/pnlAnalytics/usePnlDashboards";

export interface PnlDashboardBarProps {
  dashboards: PnlDashboardRow[];
  /** Null while the code-defined default dashboard is showing (plan decision D11). */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** True when the user owns the selected dashboard or is an organisation admin. */
  canEdit: boolean;
  editing: boolean;
  onToggleEditing: () => void;
  onCreate: (name: string) => void;
  onDuplicate: (name: string) => void;
  onRename: (name: string) => void;
  onToggleShared: () => void;
  onDelete: () => void;
  /** Shown while the default dashboard is on screen: writes real rows the user can then edit. */
  onSaveCopy: (name: string) => void;
}

const DEFAULT_DASHBOARD_OPTION = "__default__";

type DialogKind = "new" | "duplicate" | "rename" | "saveCopy" | "delete" | null;

export function PnlDashboardBar({
  dashboards,
  selectedId,
  onSelect,
  canEdit,
  editing,
  onToggleEditing,
  onCreate,
  onDuplicate,
  onRename,
  onToggleShared,
  onDelete,
  onSaveCopy,
}: PnlDashboardBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [draftName, setDraftName] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const selectId = useId();

  const selected = dashboards.find((d) => d.id === selectedId) ?? null;
  const isDefaultDashboard = selected === null;
  const selectedName = selected?.name ?? DEFAULT_DASHBOARD_NAME;

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [menuOpen]);

  const openDialog = (kind: Exclude<DialogKind, null>) => {
    setMenuOpen(false);
    setDeleteConfirmation("");
    if (kind === "duplicate") setDraftName(`Copy of ${selectedName}`);
    else if (kind === "rename") setDraftName(selectedName);
    else if (kind === "saveCopy") setDraftName(`My ${DEFAULT_DASHBOARD_NAME.toLowerCase()}`);
    else setDraftName("");
    setDialog(kind);
  };

  const closeDialog = () => {
    setDialog(null);
    setDraftName("");
    setDeleteConfirmation("");
  };

  const confirmDialog = () => {
    const name = draftName.trim();
    if (dialog === "new") onCreate(name);
    if (dialog === "duplicate") onDuplicate(name);
    if (dialog === "rename") onRename(name);
    if (dialog === "saveCopy") onSaveCopy(name);
    if (dialog === "delete") onDelete();
    closeDialog();
  };

  const nameIsUsable = draftName.trim().length > 0 && draftName.trim().length <= 80;
  const confirmDisabled = dialog === "delete" ? deleteConfirmation.trim() !== selectedName : !nameIsUsable;

  return (
    <div
      className="card"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        marginBottom: 12,
      }}
    >
      <LayoutDashboard size={16} style={{ color: "var(--color-primary)" }} aria-hidden="true" />
      <label htmlFor={selectId} style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
        Dashboard
      </label>
      <select
        id={selectId}
        className="form-input"
        style={{ width: "auto", minWidth: 220 }}
        value={selectedId ?? DEFAULT_DASHBOARD_OPTION}
        onChange={(e) => onSelect(e.target.value === DEFAULT_DASHBOARD_OPTION ? null : e.target.value)}
      >
        <option value={DEFAULT_DASHBOARD_OPTION}>{DEFAULT_DASHBOARD_NAME}</option>
        {dashboards.map((dashboard) => (
          <option key={dashboard.id} value={dashboard.id}>
            {dashboard.isShared ? `${dashboard.name} (shared)` : dashboard.name}
          </option>
        ))}
      </select>

      {isDefaultDashboard && (
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          Read only. Save a copy to start editing.
        </span>
      )}

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        {isDefaultDashboard ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openDialog("saveCopy")}>
            <Copy size={14} aria-hidden="true" /> Save a copy
          </button>
        ) : (
          canEdit && (
            <button
              type="button"
              className={editing ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
              onClick={onToggleEditing}
              aria-pressed={editing}
            >
              {editing ? <X size={14} aria-hidden="true" /> : <Pencil size={14} aria-hidden="true" />}
              {editing ? "Done editing" : "Edit"}
            </button>
          )
        )}

        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Dashboard actions"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                right: 0,
                zIndex: 30,
                minWidth: 200,
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--border-radius-md)",
                boxShadow: "var(--shadow-lg)",
                padding: 6,
              }}
            >
              <MenuItem icon={<Plus size={14} />} label="New dashboard" onClick={() => openDialog("new")} />
              <MenuItem icon={<Copy size={14} />} label="Duplicate" onClick={() => openDialog("duplicate")} />
              {canEdit && !isDefaultDashboard && (
                <>
                  <MenuItem icon={<Pencil size={14} />} label="Rename" onClick={() => openDialog("rename")} />
                  <MenuItem
                    icon={<Share2 size={14} />}
                    label={selected?.isShared ? "Stop sharing" : "Share with the organisation"}
                    onClick={() => {
                      setMenuOpen(false);
                      onToggleShared();
                    }}
                  />
                  <MenuItem
                    icon={<Trash2 size={14} />}
                    label="Delete"
                    destructive
                    onClick={() => openDialog("delete")}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={dialog !== null}
        onClose={closeDialog}
        onConfirm={confirmDialog}
        confirmDisabled={confirmDisabled}
        confirmText={dialog === "delete" ? "Delete dashboard" : "Save"}
        size="sm"
        title={
          dialog === "new"
            ? "New dashboard"
            : dialog === "duplicate"
              ? "Duplicate dashboard"
              : dialog === "rename"
                ? "Rename dashboard"
                : dialog === "saveCopy"
                  ? "Save a copy"
                  : "Delete dashboard"
        }
      >
        {dialog === "delete" ? (
          <div>
            <p style={{ color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.6 }}>
              Deleting <strong>{selectedName}</strong> removes it and every widget on it. There is no undo. Type the
              dashboard name to confirm.
            </p>
            <input
              type="text"
              className="form-input"
              value={deleteConfirmation}
              placeholder={selectedName}
              aria-label={`Type ${selectedName} to confirm the delete`}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
            />
          </div>
        ) : (
          <div>
            <label className="form-label" htmlFor="pnl-dashboard-name">
              Name
            </label>
            <input
              id="pnl-dashboard-name"
              type="text"
              className="form-input"
              value={draftName}
              maxLength={80}
              placeholder="Site profitability"
              onChange={(e) => setDraftName(e.target.value)}
            />
            {dialog === "saveCopy" && (
              <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8, lineHeight: 1.6 }}>
                This copies the {dashboards.length === 0 ? "" : "current "}company overview and its widgets into a
                dashboard you own, which you can then edit.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  destructive?: boolean;
  onClick: () => void;
}

function MenuItem({ icon, label, destructive, onClick }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "7px 9px",
        border: "none",
        borderRadius: 6,
        background: "transparent",
        // Design audit: red 700 for TEXT (6.4:1). --color-error stays for fills.
        color: destructive ? "#b91c1c" : "var(--text-primary)",
        fontSize: 13,
        textAlign: "left",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = "transparent";
      }}
    >
      {icon}
      {label}
    </button>
  );
}
